import {
  collection,
  query,
  getDocs,
  where,
  orderBy,
  doc,
  updateDoc,
  Timestamp,
  getCountFromServer,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import { db } from "../../backend/firebaseFunctions";

// User Management Functions
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const getUpdatedUsers = async (lastFetchTimestamp) => {

  try {
    const usersRef = collection(db, "users");
    // Query for users with lastUpdated > timestamp OR no lastUpdated field
    const q = query(
      usersRef,
      where("lastUpdated", ">", lastFetchTimestamp)
    );
    const querySnapshot = await getDocs(q);
    const updatedUsers = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    // Also get users without lastUpdated field
    const q2 = query(
      usersRef,
      where("lastUpdated", "==", null)
    );
    const querySnapshot2 = await getDocs(q2);
    const usersWithoutTimestamp = querySnapshot2.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    return [...updatedUsers, ...usersWithoutTimestamp];
  } catch (error) {
    console.error("Error fetching updated users:", error);
    throw error;
  }
};

// Notification Functions
export const sendTestNotification = async (userId, messageType) => {
  try {
    const functions = getFunctions();
    const testPushNotification = httpsCallable(
      functions,
      "testPushNotification"
    );

    const result = await testPushNotification({
      userId,
      messageType,
    });

    const response = {
      success: true,
      message: result.data.message || "Notification sent successfully",
    };
    return response;
  } catch (error) {
    console.error("Error sending test notification:", error);
    console.error("Error details:", {
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    throw new Error(error.message || "Failed to send notification");
  }
};

// Feedback Management Functions
export const getFeedbackReports = async () => {
  try {
    const feedbackRef = collection(db, "metadata", "feedback", "user_feedback_reports");
    const q = query(feedbackRef, orderBy("feedbackTimestamp", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error fetching feedback reports:", error);
    throw error;
  }
};

export const markFeedbackAsRead = async (feedbackId) => {
  try {
    const feedbackRef = doc(db, "metadata", "feedback", "user_feedback_reports", feedbackId);
    await updateDoc(feedbackRef, {
      wasRead: true,
      whenWasRead: new Date()
    });
  } catch (error) {
    console.error("Error marking feedback as read:", error);
    throw error;
  }
};

// Cache management functions
const CACHE_PREFIX = 'user_stats_cache_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const getCacheKey = (userId, type) => `${CACHE_PREFIX}${userId}_${type}`;

const getCachedData = (userId, type) => {
  const cacheKey = getCacheKey(userId, type);
  const cached = localStorage.getItem(cacheKey);
  if (!cached) return null;

  const { data, timestamp } = JSON.parse(cached);
  const now = Date.now();

  // Check if cache is still valid
  if (now - timestamp > CACHE_DURATION) {
    localStorage.removeItem(cacheKey);
    return null;
  }

  return data;
};

const setCachedData = (userId, type, data) => {
  const cacheKey = getCacheKey(userId, type);
  const cacheData = {
    data,
    timestamp: Date.now()
  };
  localStorage.setItem(cacheKey, JSON.stringify(cacheData));
};

// User Statistics Functions
export const getUserGroups = async (userId, forceRefresh = false) => {
  try {
    // Check cache first
    if (!forceRefresh) {
      const cachedGroups = getCachedData(userId, 'groups');
      if (cachedGroups) {
        return cachedGroups;
      }
    }

    const groupsRef = collection(db, "users", userId, "groups");
    const querySnapshot = await getDocs(groupsRef);
    const groups = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Update cache
    setCachedData(userId, 'groups', groups);
    return groups;
  } catch (error) {
    console.error("Error fetching user groups:", error);
    throw error;
  }
};

export const getUserStudents = async (userId, forceRefresh = false) => {
  try {
    // Check cache first
    if (!forceRefresh) {
      const cachedStudents = getCachedData(userId, 'students');
      if (cachedStudents) {
        return cachedStudents;
      }
    }

    const studentsRef = collection(db, "users", userId, "students");
    const querySnapshot = await getDocs(studentsRef);
    const students = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Update cache
    setCachedData(userId, 'students', students);
    return students;
  } catch (error) {
    console.error("Error fetching user students:", error);
    throw error;
  }
};

// Function to clear cache for a specific user
export const clearUserStatsCache = (userId) => {
  localStorage.removeItem(getCacheKey(userId, 'groups'));
  localStorage.removeItem(getCacheKey(userId, 'students'));
};

// Technical Logs Query Functions
export const getMobileLoginStats = async (days) => {
  try {
    const now = new Date();
    const startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));

    const logsRef = collection(db, "metadata", "global_tech_logs", "logs");
    const q = query(
      logsRef,
      where("action", "==", "mobile_user_app_open"),
      where("timestamp", ">=", Timestamp.fromDate(startDate))
    );

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error(`Error fetching mobile login stats for ${days} days:`, error);
    throw error;
  }
};

export const getWebLoginStats = async (days) => {
  try {
    const now = new Date();
    const startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));

    const logsRef = collection(db, "metadata", "global_tech_logs", "logs");
    const q = query(
      logsRef,
      where("action", "==", "web_user_app_open"),
      where("timestamp", ">=", Timestamp.fromDate(startDate))
    );

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error(`Error fetching web login stats for ${days} days:`, error);
    throw error;
  }
};

// Get daily login statistics for the past 7 days
export const getDailyLoginStats = async () => {
  try {
    // Get current date in local timezone
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const days = [];
    const dailyStats = {
      mobile: [],
      web: []
    };

    // Generate array of the last 7 days (including today)
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      days.push(date);
    }

    // Fetch data for each day efficiently
    const promises = days.map(async (date) => {
      // Create start and end of day boundaries in local timezone
      const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

      const logsRef = collection(db, "metadata", "global_tech_logs", "logs");

      // Mobile queries
      const mobileQuery = query(
        logsRef,
        where("action", "==", "mobile_user_app_open"),
        where("timestamp", ">=", Timestamp.fromDate(startOfDay)),
        where("timestamp", "<=", Timestamp.fromDate(endOfDay))
      );

      // Web queries
      const webQuery = query(
        logsRef,
        where("action", "==", "web_user_app_open"),
        where("timestamp", ">=", Timestamp.fromDate(startOfDay)),
        where("timestamp", "<=", Timestamp.fromDate(endOfDay))
      );

      const [mobileSnapshot, webSnapshot] = await Promise.all([
        getCountFromServer(mobileQuery),
        getCountFromServer(webQuery)
      ]);

      // Format date as YYYY-MM-DD without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      return {
        date: dateString,
        mobile: mobileSnapshot.data().count,
        web: webSnapshot.data().count
      };
    });

    const results = await Promise.all(promises);

    // Format data for chart
    results.forEach(day => {
      dailyStats.mobile.push({
        date: day.date,
        count: day.mobile
      });
      dailyStats.web.push({
        date: day.date,
        count: day.web
      });
    });

    return dailyStats;
  } catch (error) {
    console.error("Error fetching daily login stats:", error);
    throw error;
  }
};

// Get all login statistics for different time periods
export const getAllLoginStats = async () => {
  try {
    const [mobile24h, mobile3d, mobile7d, web24h, web3d, web7d] = await Promise.all([
      getMobileLoginStats(1),
      getMobileLoginStats(3),
      getMobileLoginStats(7),
      getWebLoginStats(1),
      getWebLoginStats(3),
      getWebLoginStats(7)
    ]);

    return {
      mobile: {
        last24h: mobile24h,
        last3d: mobile3d,
        last7d: mobile7d
      },
      web: {
        last24h: web24h,
        last3d: web3d,
        last7d: web7d
      }
    };
  } catch (error) {
    console.error("Error fetching all login stats:", error);
    throw error;
  }
};
